import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, formatTRY, todayISODate } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, Printer, FileText, Zap, CheckCircle2, Package as PackageIcon } from "lucide-react";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";
import { CAR_PARTS, SERVICES, COLORS } from "./Parts";

const STATUSES = ["Taslak", "Gönderildi", "Kabul Edildi", "Reddedildi"];
const STATUS_COLOR = {
  "Taslak": "bg-slate-100 text-slate-700 border-slate-200",
  "Gönderildi": "bg-blue-50 text-blue-800 border-blue-200",
  "Kabul Edildi": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Reddedildi": "bg-rose-50 text-rose-800 border-rose-200",
};

const emptyForm = () => ({
  customer_id: "",
  customer_name: "",
  customer_phone: "",
  plate: "",
  car_model: "",
  items: [{ _rid: crypto.randomUUID?.() || String(Math.random()), description: "", quantity: 1, unit_price: 0 }],
  discount_type: "none",
  discount_value: 0,
  vat_rate: 0.20,
  valid_until: "",
  notes: "",
  status: "Taslak",
  date: todayISODate(),
});

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteId, setDeleteId] = useState(null);
  const [pkgPickerOpen, setPkgPickerOpen] = useState(false);

  // Sihirbaz state
  const [wizOpen, setWizOpen] = useState(false);
  const [wizStep, setWizStep] = useState(1);
  const [wizPart, setWizPart] = useState("");
  const [wizService, setWizService] = useState(null);
  const [wizColor, setWizColor] = useState("");
  const [wizPrice, setWizPrice] = useState("");
  const [wizQty, setWizQty] = useState(1);

  const load = async () => {
    const [q, c, p, pk] = await Promise.all([
      api.get("/quotes"),
      api.get("/customers"),
      api.get("/parts"),
      api.get("/packages"),
    ]);
    setItems(q.data); setCustomers(c.data); setParts(p.data); setPackages(pk.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customer_id: q.customer_id || "",
      customer_name: q.customer_name || "",
      customer_phone: q.customer_phone || "",
      plate: q.plate || "",
      car_model: q.car_model || "",
      items: q.items?.length
        ? q.items.map((it) => ({ ...it, _rid: crypto.randomUUID?.() || String(Math.random()) }))
        : [{ _rid: crypto.randomUUID?.() || String(Math.random()), description: "", quantity: 1, unit_price: 0 }],
      discount_type: q.discount_type || "none",
      discount_value: q.discount_value || 0,
      vat_rate: q.vat_rate ?? 0.20,
      valid_until: q.valid_until || "",
      notes: q.notes || "",
      status: q.status,
      date: (q.date || todayISODate()).slice(0, 10),
    });
    setOpen(true);
  };

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    let discount_amount = 0;
    if (form.discount_type === "percent") discount_amount = subtotal * ((Number(form.discount_value) || 0) / 100);
    else if (form.discount_type === "amount") discount_amount = Number(form.discount_value) || 0;
    const taxable = Math.max(subtotal - discount_amount, 0);
    const vat_amount = taxable * (Number(form.vat_rate) || 0);
    const total = taxable + vat_amount;
    return { subtotal, discount_amount, taxable, vat_amount, total };
  }, [form]);

  const addRow = () =>
    setForm({
      ...form,
      items: [
        ...form.items,
        { _rid: crypto.randomUUID?.() || String(Math.random()), description: "", quantity: 1, unit_price: 0 },
      ],
    });
  const removeRow = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateRow = (i, patch) => setForm({ ...form, items: form.items.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const pickPart = (i, partId) => {
    const p = parts.find((x) => x.id === partId);
    if (!p) return;
    updateRow(i, { description: `${p.name} · ${p.category}`, unit_price: p.price });
  };

  // Sihirbaz akışı
  const resetWizard = () => {
    setWizStep(1);
    setWizPart("");
    setWizService(null);
    setWizColor("");
    setWizPrice("");
    setWizQty(1);
  };
  const openWizard = () => { resetWizard(); setWizOpen(true); };

  const wizResolved = useMemo(() => {
    if (!wizService || !wizPart) return null;
    if (wizService.requiresColor && !wizColor) return null;
    const { name, category } = wizService.resolve(wizPart, wizColor);
    const existing = parts.find((x) => x.name === name && x.category === category);
    return { name, category, existing };
  }, [wizService, wizPart, wizColor, parts]);

  useEffect(() => {
    if (wizStep === 4 && wizResolved) {
      if (wizResolved.existing) setWizPrice(String(wizResolved.existing.price));
      else if (!wizPrice) setWizPrice("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizStep, wizResolved]);

  const wizCanNext = () => {
    if (wizStep === 1) return !!wizPart;
    if (wizStep === 2) return !!wizService;
    if (wizStep === 3) return !!wizColor;
    return false;
  };

  const wizGoNext = () => {
    if (wizStep === 1 && wizPart) setWizStep(2);
    else if (wizStep === 2 && wizService) {
      if (!wizService.parts.includes(wizPart)) setWizPart(wizService.parts[0] || "");
      setWizStep(wizService.requiresColor ? 3 : 4);
    } else if (wizStep === 3 && wizColor) setWizStep(4);
  };

  const wizAddToQuote = () => {
    if (!wizResolved) return;
    const price = Number(wizPrice) || 0;
    const qty = Number(wizQty) || 1;
    const description = `${wizResolved.name} · ${wizResolved.category}`;
    // Boş ilk satırı doldur, aksi halde yeni satır ekle
    const isEmptyFirst = form.items.length === 1 && !form.items[0].description && !form.items[0].unit_price;
    if (isEmptyFirst) {
      setForm({
        ...form,
        items: [{ ...form.items[0], description, quantity: qty, unit_price: price }],
      });
    } else {
      setForm({
        ...form,
        items: [
          ...form.items,
          { _rid: crypto.randomUUID?.() || String(Math.random()), description, quantity: qty, unit_price: price },
        ],
      });
    }
    toast.success(`${wizResolved.name} eklendi (${formatTRY(price)} × ${qty})`);
    setWizOpen(false);
    resetWizard();
  };

  // Paketten ekle — tüm kalemleri teklife bir seferde ekler
  const addPackage = (pkgId) => {
    const pkg = packages.find((p) => p.id === pkgId);
    if (!pkg || !pkg.items?.length) return;
    const newLines = pkg.items.map((it) => ({
      _rid: crypto.randomUUID?.() || String(Math.random()),
      description: `${it.name}${it.category ? ` · ${it.category}` : ""}`,
      quantity: Number(it.quantity) || 1,
      unit_price: Number(it.price) || 0,
    }));
    // Boş ilk satır varsa onun yerine yaz
    const isEmptyFirst = form.items.length === 1 && !form.items[0].description && !form.items[0].unit_price;
    setForm({ ...form, items: isEmptyFirst ? newLines : [...form.items, ...newLines] });
    toast.success(`${pkg.name} eklendi · ${newLines.length} kalem`);
    setPkgPickerOpen(false);
  };

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Müşteri adı gerekli"); return; }
    if (!form.items.length) { toast.error("En az bir satır ekleyin"); return; }
    const payload = {
      customer_id: form.customer_id || "",
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      plate: form.plate,
      car_model: form.car_model,
      items: form.items.map((it) => ({
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
      })),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
      vat_rate: Number(form.vat_rate) || 0,
      valid_until: form.valid_until || null,
      notes: form.notes,
      status: form.status,
      date: form.date,
    };
    try {
      let saved;
      if (editing) {
        const r = await api.put(`/quotes/${editing.id}`, payload);
        saved = r.data;
        toast.success("Teklif güncellendi");
      } else {
        const r = await api.post("/quotes", payload);
        saved = r.data;
        toast.success("Teklif oluşturuldu");
      }
      setEditing(saved);
      load();
      return saved;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
      return null;
    }
  };

  const saveAndClose = async () => {
    const s = await save();
    if (s) setOpen(false);
  };

  const saveAndPrint = async () => {
    const s = await save();
    if (s?.id) window.open(`/print/quote/${s.id}`, "_blank");
  };

  const del = async () => { await api.delete(`/quotes/${deleteId}`); toast.success("Silindi"); setDeleteId(null); load(); };

  const printQuote = (id) => window.open(`/print/quote/${id}`, "_blank");

  return (
    <div data-testid="quotes-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Teklifler / İş Emirleri</h2>
          <p className="text-sm text-slate-500">Müşteriye teklif hazırlayın, KDV ve iskonto ekleyin, yazdırın.</p>
        </div>
        <Button data-testid="add-quote-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Teklif
        </Button>
      </div>

      <TableWrap>
        <Thead columns={["Teklif No", "Tarih", "Müşteri", "Ara Toplam", "İskonto", "KDV", "Genel Toplam", "Durum", ""]} />
        <tbody>
          {items.length === 0 && <EmptyRow colSpan={9} testId="quotes-empty" message="Henüz teklif yok." />}
          {items.map((q) => (
            <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`quote-row-${q.id}`}>
              <td className="px-4 py-3 font-mono-num text-sm font-semibold text-slate-900">{q.quote_number}</td>
              <td className="px-4 py-3 text-sm text-slate-700">{formatDate(q.date)}</td>
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-900">{q.customer_name}</div>
                {q.plate && <span className="text-xs font-mono-num text-slate-500">{q.plate}</span>}
              </td>
              <td className="px-4 py-3 text-sm font-mono-num text-slate-700">{formatTRY(q.subtotal)}</td>
              <td className="px-4 py-3 text-sm font-mono-num text-rose-600">{q.discount_amount ? `-${formatTRY(q.discount_amount)}` : "-"}</td>
              <td className="px-4 py-3 text-sm font-mono-num text-slate-700">{formatTRY(q.vat_amount)}</td>
              <td className="px-4 py-3 text-sm font-mono-num font-bold text-slate-900">{formatTRY(q.total)}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[q.status]}`}>{q.status}</span>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button data-testid={`print-quote-${q.id}`} variant="ghost" size="icon" onClick={() => printQuote(q.id)} className="h-8 w-8" title="Yazdır">
                    <Printer className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button data-testid={`edit-quote-${q.id}`} variant="ghost" size="icon" onClick={() => openEdit(q)} className="h-8 w-8" title="Düzenle">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button data-testid={`delete-quote-${q.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(q.id)} className="h-8 w-8 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="quote-dialog" className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Teklifi Düzenle · ${editing.quote_number}` : "Yeni Teklif / İş Emri"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 text-xs uppercase tracking-widest font-semibold">
                  Müşteri Bilgileri
                </Label>
                {customers.length > 0 && (
                  <Select
                    value=""
                    onValueChange={(id) => {
                      const c = customers.find((x) => x.id === id);
                      if (c) {
                        setForm({
                          ...form,
                          customer_id: c.id,
                          customer_name: c.name || "",
                          customer_phone: c.phone || "",
                          plate: c.plate || "",
                          car_model: c.car_model || "",
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="quote-customer-select" className="h-8 w-48 text-xs bg-white">
                      <SelectValue placeholder="Kayıtlı müşteriden seç…" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.plate ? `· ${c.plate}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ad Soyad *</Label>
                  <Input
                    data-testid="quote-customer-name"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input
                    data-testid="quote-customer-phone"
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value, customer_id: "" })}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Plaka</Label>
                  <Input
                    data-testid="quote-customer-plate"
                    value={form.plate}
                    onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase(), customer_id: "" })}
                    className="bg-white font-mono-num uppercase"
                  />
                </div>
                <div>
                  <Label className="text-xs">Araç Modeli</Label>
                  <Input
                    data-testid="quote-customer-model"
                    value={form.car_model}
                    onChange={(e) => setForm({ ...form, car_model: e.target.value, customer_id: "" })}
                    className="bg-white"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="quote-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Kalemler</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    data-testid="quote-package-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => setPkgPickerOpen(true)}
                    disabled={packages.length === 0}
                    className="border-blue-500 text-blue-700 hover:bg-blue-50"
                    title={packages.length === 0 ? "Henüz paket yok" : ""}
                  >
                    <PackageIcon className="w-3.5 h-3.5 mr-1" /> Paketten Ekle
                  </Button>
                  <Button
                    type="button"
                    data-testid="quote-wizard-btn"
                    variant="outline"
                    size="sm"
                    onClick={openWizard}
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                  >
                    <Zap className="w-3.5 h-3.5 mr-1" /> Sihirbaz ile Ekle
                  </Button>
                  <Button type="button" data-testid="quote-add-row" variant="outline" size="sm" onClick={addRow}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Satır Ekle
                  </Button>
                </div>
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase text-slate-500 w-56">Parçadan Seç</th>
                      <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase text-slate-500">Açıklama</th>
                      <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase text-slate-500 w-20">Adet</th>
                      <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase text-slate-500 w-32">Birim ₺</th>
                      <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase text-slate-500 w-32">Ara Toplam</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={it._rid || i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5">
                          <Select value="" onValueChange={(v) => pickPart(i, v)}>
                            <SelectTrigger data-testid={`quote-part-select-${i}`} className="h-8 text-xs"><SelectValue placeholder="Parça seç…" /></SelectTrigger>
                            <SelectContent>
                              {parts.length === 0 && <div className="px-2 py-1 text-xs text-slate-500">Parça yok</div>}
                              {parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.category} · {formatTRY(p.price)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          <Input data-testid={`quote-desc-${i}`} value={it.description} onChange={(e) => updateRow(i, { description: e.target.value })} className="h-8 text-xs" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input data-testid={`quote-qty-${i}`} type="number" step="0.01" value={it.quantity} onChange={(e) => updateRow(i, { quantity: e.target.value })} className="h-8 text-xs font-mono-num text-right" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input data-testid={`quote-unit-${i}`} type="number" step="0.01" value={it.unit_price} onChange={(e) => updateRow(i, { unit_price: e.target.value })} className="h-8 text-xs font-mono-num text-right" />
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono-num text-sm font-semibold text-slate-800">
                          {formatTRY((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                        </td>
                        <td className="px-1 py-1.5">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeRow(i)} disabled={form.items.length === 1}>
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>İskonto Tipi</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="quote-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Yok</SelectItem>
                    <SelectItem value="percent">Yüzde (%)</SelectItem>
                    <SelectItem value="amount">Tutar (₺)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>İskonto Değeri</Label>
                <Input data-testid="quote-discount-value" type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} disabled={form.discount_type === "none"} className="font-mono-num" />
              </div>
              <div>
                <Label>KDV Oranı</Label>
                <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                  <SelectTrigger data-testid="quote-vat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">KDV Yok</SelectItem>
                    <SelectItem value="0.2">%20 KDV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tarih</Label>
                <Input data-testid="quote-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Geçerlilik</Label>
                <Input data-testid="quote-valid-input" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Notlar</Label>
              <Input data-testid="quote-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ödeme koşulları, garanti vb." />
            </div>

            {/* Totals */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-1.5 text-sm font-mono-num" data-testid="quote-totals">
              <div className="flex justify-between text-slate-700"><span>Ara Toplam</span><span className="font-semibold">{formatTRY(totals.subtotal)}</span></div>
              {totals.discount_amount > 0 && (
                <div className="flex justify-between text-rose-700"><span>İskonto</span><span>-{formatTRY(totals.discount_amount)}</span></div>
              )}
              <div className="flex justify-between text-slate-700"><span>KDV Matrahı</span><span>{formatTRY(totals.taxable)}</span></div>
              <div className="flex justify-between text-slate-700"><span>KDV ({(Number(form.vat_rate) * 100).toFixed(0)}%)</span><span>{formatTRY(totals.vat_amount)}</span></div>
              <div className="flex justify-between pt-2 border-t border-slate-300 text-base font-bold text-slate-900"><span>Genel Toplam</span><span data-testid="quote-grand-total">{formatTRY(totals.total)}</span></div>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button
              data-testid="save-print-quote-btn"
              onClick={saveAndPrint}
              variant="outline"
              className="border-blue-600 text-blue-700 hover:bg-blue-50"
            >
              <Printer className="w-4 h-4 mr-2" /> Kaydet & PDF Yazdır
            </Button>
            <Button data-testid="save-quote-btn" onClick={saveAndClose} className="bg-slate-900 hover:bg-slate-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PAKET SEÇİCİ DİYALOĞU */}
      <Dialog open={pkgPickerOpen} onOpenChange={setPkgPickerOpen}>
        <DialogContent data-testid="quote-package-picker" className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PackageIcon className="w-5 h-5 text-blue-600" /> Paketten Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {packages.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                Henüz paket yok. Hizmet Paketleri sayfasından paket oluşturabilirsiniz.
              </p>
            ) : (
              packages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`quote-pkg-option-${p.id}`}
                  onClick={() => addPackage(p.id)}
                  className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      {p.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{p.description}</p>}
                      <p className="text-[11px] text-slate-500 mt-1">{p.item_count} kalem</p>
                    </div>
                    <p className="font-mono-num font-bold text-slate-900 shrink-0">{formatTRY(p.total_price || 0)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPkgPickerOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SİHİRBAZ DİYALOĞU */}
      <Dialog open={wizOpen} onOpenChange={(v) => { setWizOpen(v); if (!v) resetWizard(); }}>
        <DialogContent data-testid="quote-wiz-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> Sihirbaz ile Kalem Ekle
            </DialogTitle>
          </DialogHeader>

          {/* Adım Göstergesi */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((s) => {
              if (s === 3 && wizService && !wizService.requiresColor) return null;
              const active = wizStep === s;
              const done = wizStep > s;
              return (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${
                    done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : s === 4 ? "✓" : s}
                  </div>
                  {s < 4 && <div className={`w-8 h-0.5 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>

          <div className="space-y-4 py-2">
            {/* ADIM 1 · PARÇA */}
            {wizStep === 1 && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">1. Adım · Aracın Parçasını Seçin</Label>
                <p className="text-xs text-slate-500 mb-3">Hangi parça üzerinde işlem yapılacak?</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {CAR_PARTS.map((p) => {
                    const active = wizPart === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        data-testid={`quote-wiz-part-${p}`}
                        onClick={() => setWizPart(p)}
                        className={`px-3 py-2.5 rounded-md text-xs font-semibold border transition-all ${
                          active
                            ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ADIM 2 · SERVİS */}
            {wizStep === 2 && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">2. Adım · Yapılacak İşlem Alanı</Label>
                <p className="text-xs text-slate-500 mb-3">
                  Seçili parça: <b className="text-slate-900">{wizPart}</b>. Şimdi hangi işlem yapılacak?
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SERVICES.filter((s) => s.parts.includes(wizPart) || s.key === "sasi").map((s) => {
                    if (s.key === "komple-boya") return null;
                    if (s.key === "sasi" && wizPart !== "Şasi Ucu") return null;
                    const Icon = s.icon;
                    const active = wizService?.key === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        data-testid={`quote-wiz-service-${s.key}`}
                        onClick={() => setWizService(s)}
                        className={`px-3 py-3 rounded-md text-sm font-semibold border transition-all flex items-center gap-2 ${
                          active
                            ? `bg-gradient-to-br ${s.color} text-white border-transparent shadow-sm`
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ADIM 3 · RENK */}
            {wizStep === 3 && wizService?.requiresColor && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">3. Adım · Boya Türünü Seçin</Label>
                <p className="text-xs text-slate-500 mb-3">
                  <b className="text-slate-900">{wizPart}</b> · <b>{wizService.label}</b> için renk tipini seçin.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {COLORS.map((c) => {
                    const active = wizColor === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`quote-wiz-color-${c}`}
                        onClick={() => setWizColor(c)}
                        className={`px-4 py-4 rounded-md text-sm font-semibold border transition-all ${
                          active
                            ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ADIM 4 · ÖZET */}
            {wizStep === 4 && wizResolved && (
              <div className="space-y-4">
                <Label className="text-sm text-slate-700 font-semibold">Son Adım · Onay ve Adet</Label>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Parça / İşlem:</span>
                    <span className="font-semibold text-slate-900" data-testid="quote-wiz-preview-name">{wizResolved.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Kategori:</span>
                    <span className="font-semibold text-slate-900">{wizResolved.category}</span>
                  </div>
                  {wizResolved.existing && (
                    <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 -mx-2 px-3 py-2 rounded">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Fiyat listesinden otomatik geldi.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Birim Fiyat (₺)</Label>
                    <Input
                      data-testid="quote-wiz-price-input"
                      type="number"
                      step="0.01"
                      value={wizPrice}
                      onChange={(e) => setWizPrice(e.target.value)}
                      className="font-mono-num text-lg h-11"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label>Adet</Label>
                    <Input
                      data-testid="quote-wiz-qty-input"
                      type="number"
                      step="1"
                      min="1"
                      value={wizQty}
                      onChange={(e) => setWizQty(e.target.value)}
                      className="font-mono-num text-lg h-11"
                    />
                  </div>
                </div>
                <div className="text-right text-sm text-slate-600">
                  Ara Toplam: <span className="font-bold text-slate-900 font-mono-num">
                    {formatTRY((Number(wizPrice) || 0) * (Number(wizQty) || 1))}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row justify-between gap-2">
            <div>
              {wizStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  data-testid="quote-wiz-back-btn"
                  onClick={() => setWizStep((s) => {
                    if (s === 4 && wizService && !wizService.requiresColor) return 2;
                    return s - 1;
                  })}
                >
                  ← Geri
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setWizOpen(false); resetWizard(); }}>İptal</Button>
              {wizStep < 4 ? (
                <Button
                  data-testid="quote-wiz-next-btn"
                  onClick={wizGoNext}
                  disabled={!wizCanNext()}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  İleri →
                </Button>
              ) : (
                <Button
                  data-testid="quote-wiz-add-btn"
                  onClick={wizAddToQuote}
                  disabled={!wizPrice || Number(wizPrice) <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Teklife Ekle
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Teklif silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-quote" onClick={del} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
